/**
 * 导航配置。家长端与学生端导航项不同（见方案 2.5）：
 *  - 移动端：底部 tab 栏（拇指可达）
 *  - PC / iPad 横屏：左侧边栏
 *
 * 注意：家长端的"目标 / 打卡任务 / 统计 / 兑换审批"都挂在具体孩子下面
 * （`/students/:id/...`），没有孩子 id 时无法定位，所以它们**不进主导航**，
 * 而是从孩子详情页的入口进入 —— 主导航只放"能独立成立"的页面。
 * 奖品是个例外：它是**家长作用域**的（一个家长名下所有孩子共用一套目录），
 * 所以 `/rewards` 不依赖任何孩子 id 就能独立成立（见 ParentRewardsPage）。
 *
 * ## 2026-10-14 导航重构：「共享」→「工具」（用户原话）
 *
 * > "我觉得可以把'共享'写成'工具'，而'听写'属于一个工具，以后可能会有其他的工具，
 * >  比如英语的听写等等。针对每一个小孩的听写设置、听写布置以及听写任务的布置，
 * >  就可以放在某个具体的孩子下面。"
 *
 * 所以：
 *  - 家长主导航的第三项由「共享」变成「工具」（`/tools`），用**矢量图标**；
 *  - **语文听写的数据录入**（全家人共用一套字表）从孩子详情页搬到 `/tools/dictation`
 *    —— 它是"孩子们共用的部分"，本来就不该躲在某一个孩子下面；
 *  - **针对某个孩子的**听写设置 / 布置 / 任务（第三、四步）将来放在孩子下面，
 *    本轮不做，所以孩子详情页**不留**一个点了没用的按钮（只留注释说明去处）。
 *
 * ⚠️ `icon` 是**字符串**（emoji），`iconComponent` 是**矢量图标组件**（见
 * `components/ui/Icons.tsx`，vendored Lucide，不引图标库）。两者都在这里声明，
 * 由 `AppShell` 用一种尺寸渲染（见那里的注释）。
 *
 * 注意本文件是 `.ts`（只有数据、没有视图），所以矢量图标存的是**组件本身**
 * （`IconComponent`），不是 JSX 节点 —— JSX 在 `.ts` 里编译不过。
 */
import type { IconComponent } from '../ui/Icons';
import { IconWrench } from '../ui/Icons';

export interface NavItem {
  to: string;
  label: string;
  /** emoji 图标（历史遗留；`iconComponent` 存在时以它为准，本字段可省）。 */
  icon?: string;
  /**
   * 矢量图标**组件**（推荐）。主导航每项只给**一个**图标，
   * 两个都给时 `iconComponent` 赢 —— 避免出现"两个图标并排"。
   */
  iconComponent?: IconComponent;
  /** 'end' 用于首页，避免所有子路由都命中 "/" */
  end?: boolean;
}

export const PARENT_NAV: NavItem[] = [
  { to: '/', label: '孩子', icon: '👧', end: true },
  /**
   * 「工具」：放**孩子们共用**的东西（第一项就是语文听写的数据录入）。
   * 图标是矢量（`IconWrench`，vendored Lucide）—— 主导航每一项都在，而 emoji
   * 不认 `currentColor`，选中态的 `brand-700` 染不到它（见 Icons.tsx 的说明）。
   */
  { to: '/tools', label: '工具', iconComponent: IconWrench },
  { to: '/rewards', label: '奖品', icon: '🎁' },
  { to: '/settings', label: '设置', icon: '⚙️' },
];

export const STUDENT_NAV: NavItem[] = [
  { to: '/student', label: '今日打卡', icon: '✅', end: true },
  // 学校作业有**自己的页面**：作业节点被 `/student/today` 无条件排除
  // （`ne(nodes.kind, 'homework')`，见 checkins.service.ts），
  // 所以没有这个入口的话，孩子根本进不去作业页。
  { to: '/student/homework', label: '作业', icon: '📚' },
  { to: '/student/points', label: '我的积分', icon: '⭐' },
  { to: '/student/rewards', label: '奖品屋', icon: '🎁' },
  // 学生必须能改自己的密码：初始密码是家长创建时生成并转达的（等于共享过的秘密），
  // 且后端首登会置 mustChangePassword —— 没有入口这条要求就无法完成。
  { to: '/student/settings', label: '设置', icon: '⚙️' },
];
